import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { SET_PRODUCT_ENABLED } from '../../graphql/mutations';
import { money, useTinycmsPlans } from '../account/tinycmsPlans';
import { useAddons } from './addons';
import { SectionLocked, SectionShell } from './SectionShell';

/**
 * The two areas that sit beside Today and Content in the top bar ("tinycms UX
 * optimization review v6", Prototype v6), replacing Add-ons:
 *   Atomize  Research · Assembler · Visuals (soon). Not launched: only sites
 *            that already hold the atomizer product see it.
 *   Reach    Calendar · Campaigns · Channels. Comes with Pro; other sites see
 *            what it does and, if they own the site, the way to Pro.
 * The screens inside are the existing ones.
 */

export function AtomizeSection({ children }: { children: React.ReactNode }) {
  const { accountId, refetchAccounts } = useAccount();
  const { atomizeOn, atomizeHeld, isOwner } = useAddons();
  const [setEnabled, { loading: switching }] = useMutation(SET_PRODUCT_ENABLED);
  const [error, setError] = useState('');

  const switchTo = async (enabled: boolean) => {
    if (
      !enabled &&
      !window.confirm(
        'Turn off Atomize? Research and the Assembler stop working, and articles are no longer atomized. Your articles, pages and site are not affected, and you can turn it back on here.'
      )
    ) {
      return;
    }
    setError('');
    try {
      await setEnabled({ variables: { accountId, product: 'atomizer', enabled } });
      await refetchAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Atomize couldn’t be changed.');
    }
  };

  return (
    <SectionShell
      title="Atomize"
      badge={
        <span className="ink-not-launched" title="Not offered to customers yet">
          NOT LAUNCHED
        </span>
      }
      tabs={
        atomizeOn
          ? [
              { label: 'Research', to: '/research' },
              { label: 'Assembler', to: '/assembler' },
              { label: 'Visuals', to: '/research', soon: true },
            ]
          : []
      }
      right={
        atomizeOn && isOwner ? (
          <button
            type="button"
            className="ink-quiet-link"
            disabled={switching}
            onClick={() => void switchTo(false)}
          >
            Turn off
          </button>
        ) : null
      }
    >
      {error ? <p className="ink-section-error">{error}</p> : null}
      {atomizeOn ? (
        children
      ) : (
        <div className="ink-section-pad">
          <SectionLocked
            title={atomizeHeld ? 'Atomize is off' : 'Atomize isn’t available yet'}
            lede="It breaks each article into verified facts and the people, teams and places in it, so LLMs can cite you and your agent can reuse what you’ve already reported."
            points={[
              'Research: ask your archive a question and get a cited answer',
              'Assembler: new drafts built only from facts you’ve published',
              'Visuals, coming soon: social images and short videos from those facts',
            ]}
          >
            {atomizeHeld && isOwner ? (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={switching}
                onClick={() => void switchTo(true)}
              >
                Turn Atomize back on
              </button>
            ) : null}
          </SectionLocked>
        </div>
      )}
    </SectionShell>
  );
}

export function ReachSection({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { reachOn, isOwner, ownerEmail } = useAddons();
  const { plans } = useTinycmsPlans();
  const pro = plans.find((p) => p.key === 'pro');

  return (
    <SectionShell
      title="Reach"
      tabs={
        reachOn
          ? [
              { label: 'Calendar', to: '/calendar', match: ['/calendar', '/reach'] },
              { label: 'Campaigns', to: '/projects', match: ['/projects'] },
              { label: 'Channels', to: '/channels' },
            ]
          : []
      }
    >
      {reachOn ? (
        children
      ) : (
        <div className="ink-section-pad">
          <SectionLocked
            title="Reach comes with Pro"
            lede="Your agent turns what you publish into social posts and campaigns. Nothing goes out until you approve it."
            points={[
              'Posts for X, LinkedIn, Threads and more, drafted from each article',
              'Campaigns built around what you publish',
              'One calendar where you approve or skip every post',
            ]}
          >
            {isOwner ? (
              <div className="ink-locked-cta">
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  onClick={() => navigate('/settings?section=billing')}
                >
                  See Pro
                </button>
                {pro ? (
                  <span className="ink-muted">{money(pro.yearlyPrice)} a month, billed yearly</span>
                ) : null}
              </div>
            ) : (
              <p className="ink-muted">
                Only the site owner{ownerEmail ? `, ${ownerEmail},` : ''} can change the plan.
              </p>
            )}
          </SectionLocked>
        </div>
      )}
    </SectionShell>
  );
}
